import {
  LIBRARY_STATUSES,
  LIBRARY_STATUS_LABELS_PL,
  type LibraryEntry,
  type LibraryStatus,
} from "@anisync/types";
import { FileUpIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { PageLoading } from "../components/ui/loading";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { getLibrary } from "../lib/api";
import { fadeIn, stagger } from "../lib/motion";
import { useSession } from "../lib/use-session";

/** Stałe klucze wierszy zastępczych — indeks tablicy nie nadaje się na klucz. */
const SKELETON_KEYS = ["r1", "r2", "r3", "r4"] as const;

/** Kolor plakietki wynika ze statusu, żeby lista czytała się bez czytania. */
const STATUS_VARIANT: Record<
  LibraryStatus,
  "accent" | "success" | "warning" | "destructive" | "outline"
> = {
  watching: "accent",
  completed: "success",
  paused: "warning",
  dropped: "destructive",
  planning: "outline",
};

/**
 * Biblioteka użytkownika, z zakładkami po statusie.
 *
 * Filtrowanie po stronie klienta: całość i tak przychodzi jednym zapytaniem
 * (biblioteka jednej osoby to setki, nie miliony pozycji), a przełączanie
 * zakładek bez żądania jest natychmiastowe.
 */
export function LibraryScreen() {
  const { loading, session } = useSession();
  const [entries, setEntries] = useState<LibraryEntry[] | null>(null);
  const [tab, setTab] = useState<LibraryStatus | "all">("all");

  const authenticated = session?.authenticated === true;

  useEffect(() => {
    if (!authenticated) return;
    let cancelled = false;
    getLibrary()
      .then((data) => {
        if (!cancelled) setEntries(data.items);
      })
      .catch(() => {
        if (!cancelled) setEntries([]);
      });
    return () => {
      cancelled = true;
    };
  }, [authenticated]);

  if (loading) return <PageLoading />;

  if (!authenticated) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Biblioteka</CardTitle>
          <CardDescription>
            Biblioteka i postęp oglądania są przypisane do konta, więc wymagają zalogowania.
            Przycisk logowania jest w prawym górnym rogu.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const all = entries ?? [];
  const shown = tab === "all" ? all : all.filter((e) => e.status === tab);
  const counts = new Map<LibraryStatus, number>();
  for (const entry of all) counts.set(entry.status, (counts.get(entry.status) ?? 0) + 1);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Biblioteka</h1>
        {/* Wejście do importu jest tutaj, a nie w nawigacji głównej: to czynność
            jednorazowa, a najbardziej przydaje się dokładnie w momencie, w którym
            widać, że biblioteka jest pusta. */}
        <Button variant="outline" size="sm" asChild>
          <Link to="/import">
            <FileUpIcon />
            Import z MyAnimeList
          </Link>
        </Button>
      </div>

      {/* Zakładki tylko przestawiają filtr nad jedną listą, więc treść zostaje
          poza `TabsContent` — inaczej każdy panel powtarzałby ten sam kod. */}
      <Tabs value={tab} onValueChange={(v) => setTab(v as LibraryStatus | "all")}>
        <TabsList>
          <TabsTrigger value="all">Wszystko ({all.length})</TabsTrigger>
          {LIBRARY_STATUSES.map((s) => (
            <TabsTrigger key={s} value={s}>
              {LIBRARY_STATUS_LABELS_PL[s]} ({counts.get(s) ?? 0})
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {entries === null && (
        <ul className="row-grid gap-2" aria-label="Wczytuję bibliotekę">
          {SKELETON_KEYS.map((k) => (
            <li key={k}>
              <Skeleton className="h-[5.5rem] rounded-xl" />
            </li>
          ))}
        </ul>
      )}

      {entries !== null && shown.length === 0 && (
        <p className="text-muted-foreground">
          {all.length === 0
            ? "Biblioteka jest pusta. Zapełnia się sama, gdy zaczniesz oglądać — albo dodaj serię ręcznie na jej stronie."
            : "Nic w tej kategorii."}
        </p>
      )}

      {/* Karty obok siebie (`row-grid`), nie wiersz przez całe okno: na szerokim
          ekranie okładka i licznik odcinków stały na dwóch końcach pustego pasa. */}
      <ul className="row-grid gap-2">
        {shown.map((entry, i) => (
          <li key={entry.titleId} className="enter-rise" style={stagger(i)}>
            <Link
              to={`/tytul/${entry.titleId}`}
              className="focus-ring-own flex items-center gap-4 rounded-xl border border-border bg-card p-3 outline-none transition-colors duration-150 hover:border-primary/40 hover:bg-secondary/40 focus-visible:ring-[0.1875rem] focus-visible:ring-ring/50"
            >
              {entry.title.coverUrl !== null && (
                <img
                  {...fadeIn}
                  src={entry.title.coverUrl}
                  alt=""
                  loading="lazy"
                  className="h-20 w-14 shrink-0 rounded-md bg-secondary object-cover"
                />
              )}
              <div className="flex min-w-0 flex-col gap-1.5">
                <span className="truncate font-medium">
                  {entry.title.titleEnglish ?? entry.title.titleRomaji}
                </span>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={STATUS_VARIANT[entry.status]}>
                    {LIBRARY_STATUS_LABELS_PL[entry.status]}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    {entry.episodesWatched}
                    {entry.title.episodesTotal !== null ? ` / ${entry.title.episodesTotal}` : ""}{" "}
                    odc.
                    {entry.score !== null ? ` · ocena ${entry.score}` : ""}
                  </span>
                </div>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
